import React, { useCallback } from "react";
import useAudioAdapter from "@/common/hooks/useAudioAdapter.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import useUserIsWriting from "@/common/hooks/useUserIsWriting.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { ModalTypes } from "@/enum/modalTypes.ts";
import {
  AUDIOCONTROL_KEYBOARD_SHORTCUT_ALLOWED_MODAL_TYPES, KeyboardEventHandler, KeyboardEventHandlerOptions
} from "@/types/types.ts";

export function isAllowedModalType(
  modalType: string | null,
  allowedTypes: ModalTypes[]
): boolean {
  if (!modalType) return false;

  const allowedTypeStrings = allowedTypes.map(type => type.toString());

  return allowedTypeStrings.includes(modalType);
}

export const useKeyboardShortcutAudioControl = (
  {
    forceEnable = false,
    forceDisable = false,
    ignoreWriting = false,
    ignoreModals = false,
    allowedModalTypes = AUDIOCONTROL_KEYBOARD_SHORTCUT_ALLOWED_MODAL_TYPES
  }: KeyboardEventHandlerOptions = {}) => {
  const [modalType] = useSearchParamState(URLSearchParamKeys.MODAL, { defaultValue: null });
  const userIsWriting = useUserIsWriting();
  const [audioAdapter] = useAudioAdapter();

  const canTriggerShortcut = useCallback(() => {
    if (forceEnable) return true;

    if (forceDisable) return false;

    const isWriting = !ignoreWriting && userIsWriting();
    if (isWriting) return false;

    const isModalOpen = !!modalType;
    if (isModalOpen && !ignoreModals) {
      if (isAllowedModalType(modalType, allowedModalTypes)) {
        return true;
      }
      return false;
    }

    return true;
  }, [
    forceEnable,
    forceDisable,
    ignoreWriting,
    ignoreModals,
    userIsWriting,
    modalType,
    allowedModalTypes
  ]);

  // React naar DOM event type conversie
  const withShortcutCheck = useCallback(
    (handler: KeyboardEventHandler): (event: KeyboardEvent) => void => {
      return (event: KeyboardEvent) => {
        if (!canTriggerShortcut()) return;

        // Creëer een meegevende "synthetische" event om de typesystemen te overbruggen
        // Dit is een type-cast hack die TypeScript laat geloven dat we het juiste type hebben
        // In de werkelijkheid gebruiken we gewoon het DOM event, maar TypeScript ziet het als React event
        const syntheticEvent = event as unknown as React.KeyboardEvent<Element>;

        handler(syntheticEvent);
      };
    },
    [canTriggerShortcut]
  );

  return {
    canTriggerShortcut,
    withShortcutCheck,
    audioAdapter
  };
};
